import { useQuery } from "@tanstack/react-query";

import { dashboardRequest } from "../providers/aws/client";
import {
  isActive,
  type PlaygroundJob,
  type PlaygroundRequest,
  type PlaygroundStatus,
} from "./playground";

const base = "/lab-playground";

export const getPlaygroundStatus = () =>
  dashboardRequest<PlaygroundStatus>(`${base}/status`);

export const listPlaygroundJobs = (experiment: string) =>
  dashboardRequest<{ experiment: string; jobs: PlaygroundJob[] }>(
    `${base}/jobs?experiment=${encodeURIComponent(experiment)}&limit=60`,
  );

export const createPlaygroundJobs = (
  request: PlaygroundRequest,
  idempotencyKey: string,
) =>
  dashboardRequest<{ batch_id: string; jobs: PlaygroundJob[] }>(
    `${base}/jobs`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(request),
    },
  );

export const cancelPlaygroundJob = (experiment: string, jobId: string) =>
  dashboardRequest<{ job: PlaygroundJob }>(
    `${base}/jobs/${encodeURIComponent(experiment)}/${encodeURIComponent(jobId)}/cancel`,
    { method: "POST", body: "{}" },
  );

type UploadIntent = {
  upload: { url: string; fields: Record<string, string> };
  object: { key: string; mime_type: string };
};

/** Presigned POST straight to the Labs bucket; returns the key a job can name. */
export async function uploadPlaygroundImage(file: File): Promise<string> {
  const intent = await dashboardRequest<UploadIntent>(`${base}/uploads`, {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      mime_type: file.type,
      size_bytes: file.size,
    }),
  });
  const form = new FormData();
  Object.entries(intent.upload.fields).forEach(([name, value]) =>
    form.append(name, value),
  );
  form.append("file", file);
  const response = await fetch(intent.upload.url, {
    method: "POST",
    body: form,
  });
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
  return intent.object.key;
}

export function usePlaygroundStatus() {
  return useQuery({
    queryKey: ["lab-playground", "status"],
    queryFn: getPlaygroundStatus,
    refetchInterval: 10_000,
    retry: false,
  });
}

/**
 * The entry's generation history. Polls every 3 s while anything is queued or rendering, else every
 * 5 min: the API presigns image URLs for an hour, so a refresh keeps an open page's images loading.
 */
export function usePlaygroundJobs(experiment: string) {
  return useQuery({
    queryKey: ["lab-playground", "jobs", experiment],
    queryFn: () => listPlaygroundJobs(experiment),
    refetchInterval: (query) =>
      query.state.data?.jobs.some(isActive) ? 3_000 : 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}
